import React, { useState } from 'react';
import { useDispatch } from 'react-redux';
import { Box, Typography, IconButton, Menu, MenuItem, Chip, Tooltip, Dialog, DialogContent, CardMedia } from '@material-ui/core';
import { GetApp, Delete, Visibility, MoreVert, InsertDriveFile, Image, PictureAsPdf, VideoLibrary, Audiotrack, Description, TableChart, Code } from '@material-ui/icons';
import { makeStyles } from '@material-ui/core/styles';
import { removeAttachment } from '../../store/Activities';
import axios from 'axios';
import moment from 'moment';
import { Modal, ModalHeader, ModalBody } from 'reactstrap';

const useStyles = makeStyles((theme) => ({
  attachmentList: { padding: theme.spacing(0.5), display: 'flex', flexDirection: 'column', gap: theme.spacing(0.5) },
  attachmentItem: {
    display: 'flex',
    alignItems: 'center',
    padding: theme.spacing(0.75, 1),
    borderRadius: 6,
    backgroundColor: '#F8F9FF',
    border: `1px solid #D7DCFF`,
    minHeight: 36,
    maxWidth: 350,
    transition: 'all 0.2s ease',
    cursor: 'pointer',
    position: 'relative',
    marginBottom: '5px',
    '&:hover': {
      backgroundColor: '#E5EAFE70',
      transform: 'translateY(-1px)',
      boxShadow: '0 2px 4px rgba(0,0,0,0.01)'
    }
  },
  fileIconWrapper: {
    width: 28,
    height: 28,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    borderRadius: 4,
    marginRight: theme.spacing(1),
    flexShrink: 0,
  },
  fileIcon: { fontSize: 20 },
  imageIcon: { color: '#e91e63', backgroundColor: '#fce4ec' },
  pdfIcon: { color: '#f44336', backgroundColor: '#ffebee' },
  videoIcon: { color: '#9c27b0', backgroundColor: '#f3e5f5' },
  audioIcon: { color: '#ff9800', backgroundColor: '#fff3e0' },
  documentIcon: { color: '#2196f3', backgroundColor: '#e3f2fd' },
  spreadsheetIcon: { color: '#4caf50', backgroundColor: '#e8f5e9' },
  codeIcon: { color: '#00bcd4', backgroundColor: '#e0f7fa' },
  defaultIcon: { color: '#607d8b', backgroundColor: '#eceff1' },
  fileInfo: {
    flex: 1,
    minWidth: 0,
    display: 'flex',
    flexDirection: 'column',
    justifyContent: 'center'
  },
  fileName: {
    fontSize: '0.8125rem',
    fontWeight: 400,
    lineHeight: 1.2,
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    whiteSpace: 'nowrap',
    color: '#333'
  },
  fileMeta: {
    display: 'flex',
    alignItems: 'center',
    gap: theme.spacing(0.5),
    marginTop: 2,
    fontSize: '0.6875rem',
    color: '#666'
  },
  fileSize: {
    fontSize: '0.6875rem',
    height: 16,
    '& .MuiChip-label': { padding: '0 6px' }
  },
  previewDialog: { '& .MuiDialog-paper': { maxWidth: '90vw', maxHeight: '90vh' } },
  previewImage: { maxWidth: '100%', maxHeight: '80vh', objectFit: 'contain' },
  previewThumbnail: {
    position: 'absolute',
    top: '50%',
    right: 28,
    width: 25,
    height: 25,
    borderRadius: 4,
    overflow: 'hidden',
    border: '1px solid #D7DCFF',
    transition: 'all 0.2s ease',
    pointerEvents: 'none',
    transform: 'translateY(-50%)',
    zIndex: 1
  },
  showPreview: {
    '& $previewThumbnail': {
      boxShadow: '0 2px 8px rgba(0,0,0,0.10)'
    }
  },
  noAttachments: { textAlign: 'center', padding: theme.spacing(4), color: theme.palette.text.secondary }
}));

const getFileIcon = (mimeType, classes) => {
  let Icon = InsertDriveFile;
  let className = classes.defaultIcon;

  if (!mimeType) return { Icon, className };

  if (mimeType.startsWith('image/')) {
    Icon = Image;
    className = classes.imageIcon;
  } else if (mimeType === 'application/pdf') {
    Icon = PictureAsPdf;
    className = classes.pdfIcon;
  } else if (mimeType.startsWith('video/')) {
    Icon = VideoLibrary;
    className = classes.videoIcon;
  } else if (mimeType.startsWith('audio/')) {
    Icon = Audiotrack;
    className = classes.audioIcon;
  } else if (
    mimeType === 'application/msword' ||
    mimeType === 'application/vnd.openxmlformats-officedocument.wordprocessingml.document' ||
    mimeType.startsWith('text/')
  ) {
    Icon = Description;
    className = classes.documentIcon;
  } else if (
    mimeType === 'application/vnd.ms-excel' ||
    mimeType === 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' ||
    mimeType === 'text/csv'
  ) {
    Icon = TableChart;
    className = classes.spreadsheetIcon;
  } else if (
    mimeType === 'application/javascript' ||
    mimeType === 'application/json' ||
    mimeType === 'application/xml' ||
    mimeType.includes('script') ||
    mimeType.includes('code')
  ) {
    Icon = Code;
    className = classes.codeIcon;
  }

  return { Icon, className };
};

const formatFileSize = (bytes) => {
  if (!bytes || bytes === 0 || isNaN(bytes)) return '0 Bytes';
  const k = 1024;
  const sizes = ['Bytes', 'KB', 'MB', 'GB'];
  const i = Math.floor(Math.log(bytes) / Math.log(k));
  return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i];
};

const AttachmentList = ({ attachments = [], onDelete, showUploadedBy = true, enableActions = true }) => {
  const classes = useStyles();
  const dispatch = useDispatch();
  const deletingRef = React.useRef(false);
  const [anchorEl, setAnchorEl] = useState(null);
  const [selectedAttachment, setSelectedAttachment] = useState(null);
  const [previewOpen, setPreviewOpen] = useState(false);
  const [previewUrl, setPreviewUrl] = useState('');
  const [thumbnailUrls, setThumbnailUrls] = useState({});
  const [hoveredAttachment, setHoveredAttachment] = useState(null);
  const [deleteModalOpen, setDeleteModalOpen] = useState(false);
  const [modalDroppedAlert, setModalDroppedAlert] = useState(false);
  const [modalViewFileError, setModalViewFileError] = useState(false);
  const [modalDeleteFileError, setModalDeleteFileError] = useState(false);

  const handleMenuOpen = (event, attachment) => {
    setAnchorEl(event.currentTarget);
    setSelectedAttachment(attachment);
  };

  const handleMenuClose = () => {
    setAnchorEl(null);
    // Don't clear selectedAttachment - keep it for delete modal
  };

  const handleDownload = async () => {
    if (!selectedAttachment) return;

    const attachment = selectedAttachment;
    handleMenuClose();

    try {
      const response = await axios.get(`/attachments/${attachment['_id']}/signed-url`, { params: { download: 'true' }});
      const signedUrl = response['data']['body']?.['url'] || response['data']['url'];
      if (!signedUrl) { setModalDroppedAlert(true);
        return; }

      const link = document.createElement('a');
      link.href = signedUrl;
      link.download = attachment['originalName'] || attachment['fileName'] || attachment['name'] || 'download';
      link.style.display = 'none';
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
    } catch (error) {
      console.error('Error downloading file:', error);
      setModalDroppedAlert(true);
    }
  };

  const handleView = async (attachment) => {
    try {
      console.log('Viewing attachment:', attachment['_id']);
      const response = await axios.get(`/attachments/${attachment['_id']}/signed-url`);
      const signedUrl = response['data']['body']['url'];

      // Open in new tab
      window.open(signedUrl, '_blank');
    } catch (error) {
      console.error('Error viewing file:', error);
      setModalViewFileError(true);
    }
    handleMenuClose();
  };

  const handleDelete = () => {
    if (!selectedAttachment || deletingRef.current) return;

    deletingRef.current = true;

    // Close modal immediately
    setDeleteModalOpen(false);
    // Dispatch action - the saga will handle the API call
    dispatch(removeAttachment(selectedAttachment['_id']));
    // Call parent callback
    if (onDelete) onDelete(selectedAttachment['_id']);
    // Clear selection
    setSelectedAttachment(null);
    // Reset flag
    setTimeout(() => { deletingRef.current = false; }, 500); };

  const handlePreview = async (attachment) => {
    if (attachment['mimeType']?.startsWith('image/')) {
      try {
        const response = await axios.get(`/attachments/${attachment['_id']}/signed-url`);
        setPreviewUrl(response['data']['body']['url']);
        setPreviewOpen(true);
      } catch (error) {
        console.error('Error getting preview URL:', error);
      }
    }
    handleMenuClose();
  };

  const loadThumbnail = async (attachment) => {
    if (attachment['mimeType']?.startsWith('image/') && !thumbnailUrls[attachment['_id']]) {
      try {
        const response = await axios.get(`/attachments/${attachment['_id']}/signed-url`);
        setThumbnailUrls(prev => ({
          ...prev,
          [attachment['_id']]: response['data']['body']['url']
        }));
      } catch (error) {
        console.error('Error loading thumbnail:', error);
      }
    }
  };

  const handleMouseEnter = (attachment) => {
    setHoveredAttachment(attachment['_id']);
  };

  const handleMouseLeave = () => {
    setHoveredAttachment(null);
  };

  React.useEffect(() => {
    attachments.forEach(attachment => { if (attachment['mimeType']?.startsWith('image/')) { loadThumbnail(attachment); } }); }, [attachments]);
  if (attachments.length === 0) {
    return (
      <Box className={classes.noAttachments}>
        <InsertDriveFile style={{ fontSize: 48, marginBottom: 8 }} />
        <Typography variant="body2">No attachments yet</Typography>
      </Box>
    );
  }

  // console.log('**************attachments', attachments);
  return (
    <div className='attchment-sidebar'>
      <Box className={classes.attachmentList}>
        {attachments.map((attachment) => {
          const { Icon, className } = getFileIcon(attachment['mimeType'] || attachment['type'], classes);
          return (
            <Box
              key={attachment['_id']}
              className={`${classes.attachmentItem} ${hoveredAttachment === attachment['_id'] ? classes.showPreview : ''}`}
              onClick={() => handleView(attachment)}
              onMouseEnter={() => handleMouseEnter(attachment)}
              onMouseLeave={handleMouseLeave}
            >
              <Box className={`${classes.fileIconWrapper} ${className}`}>
                <Icon className={classes.fileIcon} />
              </Box>
              <Box className={classes.fileInfo}>
                <div className='title-area'>
                  <h5 className={classes.fileName}> {attachment['originalName'] || attachment['fileName'] || attachment['name'] || 'Unnamed file'}</h5>

                </div>
                <div className='detail-area'>
                  {!showUploadedBy && (
                    <Typography variant="caption">
                      {moment(attachment['createdAt']).format('MMM D, YYYY')}
                    </Typography>
                  )}
                  {showUploadedBy && attachment['uploadedBy'] && (
                    <Typography variant="caption">
                      {attachment['uploadedBy']['firstName']} • {moment(attachment['createdAt']).format('MMM D')} • {formatFileSize(attachment['fileSize'] || attachment['size'] || 0)}
                    </Typography>
                  )}
                </div>
              </Box>
              {attachment['mimeType']?.startsWith('image/') && (
                <Box className={classes.previewThumbnail}>
                  {thumbnailUrls[attachment['_id']] ? (
                    <CardMedia
                      component="img"
                      image={thumbnailUrls[attachment['_id']]}
                      alt="Preview"
                      style={{ width: '100%', height: '100%', objectFit: 'cover' }}
                    />
                  ) : (
                    <Box style={{ width: '100%', height: '100%', backgroundColor: '#f0f0f0', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                      <Image style={{ fontSize: 16, color: '#999' }} />
                    </Box>
                  )}
                </Box>
              )}
              {enableActions && (
                <IconButton
                  size="small"
                  onClick={(e) => {
                    e.stopPropagation();
                    handleMenuOpen(e, attachment);
                  }}
                  style={{ padding: 4 }}
                >
                  <MoreVert style={{ fontSize: 18 }} />
                </IconButton>
              )}
            </Box>
          );
        })}

        <Menu anchorEl={anchorEl} open={Boolean(anchorEl)} onClose={handleMenuClose} classes={{ paper: "attchement-dropdpwon" }}>
          <MenuItem onClick={() => handleView(selectedAttachment)}>
            <Visibility fontSize="small" style={{ marginRight: 8 }} />
            View
          </MenuItem>
          <MenuItem onClick={handleDownload}>
            <GetApp fontSize="small" style={{ marginRight: 8 }} />
            Download
          </MenuItem>
          {selectedAttachment?.['mimeType']?.startsWith('image/') && (
            <MenuItem onClick={() => handlePreview(selectedAttachment)}>
              <Visibility fontSize="small" style={{ marginRight: 8 }} />
              Preview
            </MenuItem>
          )}
          <MenuItem onClick={() => { handleMenuClose(); setDeleteModalOpen(true); }}>
            <Delete fontSize="small" style={{ marginRight: 8 }} />
            Delete
          </MenuItem>
        </Menu>

        <Dialog open={previewOpen} onClose={() => setPreviewOpen(false)} className={classes.previewDialog}>
          <DialogContent>
            <img src={previewUrl} alt="Preview" className={classes.previewImage} />
          </DialogContent>
        </Dialog>

        <Modal isOpen={deleteModalOpen} toggle={() => { setDeleteModalOpen(false); setSelectedAttachment(null);
        }} className={`main-modal new-user-modal modal-dialog-centered endcall-modal`}>
          <ModalHeader toggle={() => {
            setDeleteModalOpen(false);
            setSelectedAttachment(null);
          }}>
            <h3>Confirm delete</h3>
          </ModalHeader>
          <ModalBody>
            <div className='add-content mt-4'>
              <div className='group-from'>
                <p>Are you sure you want to delete <strong>{selectedAttachment?.['originalName'] || selectedAttachment?.['fileName'] || 'this file'}</strong>?</p>
              </div>

              <div className='group-from btn-group mt-4'>
                <button
                  type="button"
                  className='btn-style-new'
                  onClick={(e) => {
                    console.log('[DELETE BUTTON] Clicked');
                    e.preventDefault();
                    e.stopPropagation();
                    handleDelete();
                  }}
                >
                  Delete
                </button>
                <button
                  type="button"
                  className='btn-style-border'
                  onClick={() => {
                    setDeleteModalOpen(false);
                    setSelectedAttachment(null);
                  }}
                >
                  Cancel
                </button>
              </div>
            </div>
          </ModalBody>
        </Modal>

         {/* --------------- Altert Dropped--------------- */}
         <Modal isOpen={modalDroppedAlert} toggle={() => setModalDroppedAlert(false)} className={`main-modal new-user-modal modal-dialog-centered`}>
            <ModalHeader toggle={() => setModalDroppedAlert(false)}></ModalHeader>
            <ModalBody>
               <div className='add-content'>
                  <h2>Failed to download file. Please try again.</h2>
                  <div className='templates-inner'>
                     <div className='btn-group'>
                        <button className='btn-style-new' onClick={() => setModalDroppedAlert(false)}>Ok</button>
                     </div>
                  </div>
               </div>
            </ModalBody>
         </Modal>

         {/* --------------- Altert Dropped--------------- */}
         <Modal isOpen={modalViewFileError} toggle={() => setModalViewFileError(false)} className={`main-modal new-user-modal modal-dialog-centered`}>
            <ModalHeader toggle={() => setModalViewFileError(false)}></ModalHeader>
            <ModalBody>
               <div className='add-content'>
                  <h2>Failed to view file. Please try again.</h2>
                  <div className='templates-inner'>
                     <div className='btn-group'>
                        <button className='btn-style-new' onClick={() => setModalViewFileError(false)}>Ok</button>
                     </div>
                  </div>
               </div>
            </ModalBody>
         </Modal>

         <Modal isOpen={modalDeleteFileError} toggle={() => setModalDeleteFileError(false)} className={`main-modal new-user-modal modal-dialog-centered`}>
            <ModalHeader toggle={() => setModalDeleteFileError(false)}></ModalHeader>
            <ModalBody>
               <div className='add-content'>
                  <h2>Failed to delete file. Please try again.</h2>
                  <div className='templates-inner'>
                     <div className='btn-group'>
                        <button className='btn-style-new' onClick={() => setModalDeleteFileError(false)}>Ok</button>
                     </div>
                  </div>
               </div>
            </ModalBody>
         </Modal>

      </Box>
    </div>
  );
};

export default AttachmentList;